/**
 * Radar / Spider Chart Type Definition
 * Multi-dimensional comparison of models across normalized axes
 */

import {
  titleControl,
} from '../shared/controls';

import {
  matplotlibSetup,
  finishCode,
} from '../shared/codeSnippets';

import {
  seabornSetup,
} from '../shared/seabornSnippets';

import { getColors } from '../shared/palettes';

// Sample data: multi-dimensional LLM benchmark (all scores 0–100)
const SAMPLE_DATA = [
  {
    name: 'Series A',
    values: { 'Metric A': 92, 'Metric B': 78, 'Metric C': 88, 'Metric D': 85, 'Metric E': 90, 'Metric F': 86 },
  },
  {
    name: 'Series B',
    values: { 'Metric A': 91, 'Metric B': 65, 'Metric C': 62, 'Metric D': 80, 'Metric E': 88, 'Metric F': 93 },
  },
  {
    name: 'Series C',
    values: { 'Metric A': 93, 'Metric B': 58, 'Metric C': 70, 'Metric D': 82, 'Metric E': 95, 'Metric F': 91 },
  },
];

const radar = {
  id: 'radar',
  name: 'Radar Chart',
  category: 'radar',
  description: 'Multi-dimensional model comparison across normalized axes',

  getSampleData: () => SAMPLE_DATA,
  sampleData: SAMPLE_DATA,

  defaultConfig: {
    externalColorMode: 'palette',
    colorPalette: 'blues',
    fillAlpha: 0.2,
    showAxisLabels: true,
    showValues: false,
    showLegend: true,
    title: '',
    outputFormat: 'matplotlib',
  },

  controls: [
    {
      key: 'colorPalette',
      label: 'Color Palette',
      type: 'select',
      options: [
        { label: 'Blues', value: 'blues' },
        { label: 'Greens', value: 'greens' },
        { label: 'Reds', value: 'reds' },
      ],
    },
    {
      key: 'fillAlpha',
      label: 'Fill Opacity',
      type: 'slider',
      min: 0,
      max: 0.6,
      step: 0.05,
      unit: '',
    },
    {
      key: 'showAxisLabels',
      label: 'Show Axis Labels',
      type: 'toggle',
    },
    {
      key: 'showValues',
      label: 'Show Values at Vertices',
      type: 'toggle',
    },
    {
      key: 'showLegend',
      label: 'Show Legend',
      type: 'toggle',
    },
    titleControl,
  ],

  generateCode: (config, dataContext, s) => {
    const hasUserData = dataContext?.isLoaded && dataContext.mapping?.series;
    const fillAlpha = config.fillAlpha ?? 0.2;

    const axes = Object.keys(SAMPLE_DATA[0].values);

    const colors = getColors(config, SAMPLE_DATA.length, s);

    const seriesDataCode = SAMPLE_DATA.map(s => {
      const vals = axes.map(a => s.values[a]);
      return `    ('${s.name}', [${vals.join(', ')}])`;
    }).join(',\n');

    const categoriesCode = JSON.stringify(axes);
    const colorsCode = JSON.stringify(colors);

    const dataSection = hasUserData
      ? `df = pd.read_csv('${dataContext.fileName}')
# Wide format: one row per series, one column per axis
series_col = '${dataContext.mapping.series}'
categories = [c for c in df.columns if c != series_col]
series_data = [(row[series_col], [row[c] for c in categories]) for _, row in df.iterrows()]`
      : `categories = ${categoriesCode}
series_data = [
${seriesDataCode}
]`;

    const legendCode = config.showLegend
      ? `ax.legend(loc='upper right', bbox_to_anchor=(1.4, 1.1), fontsize=${s?.legendSize || 10}, frameon=True)`
      : '# Legend disabled';

    const titleCodeStr = config.title
      ? `fig.suptitle('${config.title}', fontsize=${s?.titleSize || 14}, y=1.02, ha='center', color='${s?.titleColor || '#000000'}')`
      : '';

    const codeBody = `${matplotlibSetup(s)}
import pandas as pd

# ======== DATA ========
${dataSection}
# ======================

N = len(categories)
max_per_axis = [max([values[idx] for _, values in series_data] + [1]) for idx in range(N)]
# Evenly spaced angles, starting from the top (−π/2)
angles = [(-np.pi / 2 + i / N * 2 * np.pi) for i in range(N)]
angles_closed = angles + [angles[0]]  # close the polygon

colors = ${colorsCode}

fig = plt.figure(figsize=(6, 6), dpi=300)
fig.patch.set_facecolor('${s?.figBackground || '#FFFFFF'}')
ax = fig.add_subplot(111, polar=True)

# Rotate so first axis is at the top
ax.set_theta_offset(np.pi / 2)
ax.set_theta_direction(-1)

for i, (name, values) in enumerate(series_data):
    normalized_values = [(value / max_per_axis[idx]) if max_per_axis[idx] else 0 for idx, value in enumerate(values)]
    vals_closed = normalized_values + [normalized_values[0]]
    ax.plot(np.linspace(0, 2 * np.pi, N, endpoint=False).tolist() + [0],
            vals_closed, color=colors[i], linewidth=2, label=name)
    ax.fill(np.linspace(0, 2 * np.pi, N, endpoint=False),
            normalized_values, color=colors[i], alpha=${fillAlpha})

ax.set_xticks(np.linspace(0, 2 * np.pi, N, endpoint=False))
ax.set_xticklabels(${config.showAxisLabels ? 'categories' : "[''] * N"}, fontsize=11)
ax.set_ylim(0, 1)
ax.set_yticklabels([])  # hide radial tick labels
ax.grid(alpha=${s?.gridAlpha || 0.3}, color='${s?.gridColor || '#CCCCCC'}', linestyle='--', linewidth=0.5)
${legendCode}
${titleCodeStr}
${finishCode('radar_chart')}`;

    if (config.outputFormat === 'seaborn') {
      // Seaborn doesn't support polar axes — use matplotlib with seaborn styling
      return codeBody.replace(matplotlibSetup(s), `${seabornSetup(s)}
import pandas as pd`);
    }

    return codeBody;
  },
};

export default radar;
